import type {
  FetchSchoolCalendarListParams,
  SchoolCalendar,
  SchoolCalendarListResponse
} from 'Services/SchoolManagement/Features/SchoolCalendar/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum SchoolCalendarTagTypes {
  SCHOOL_CALENDAR = 'SchoolCalendar'
}

export const schoolCalendarApi = createBaseApi({
  reducerPath: 'schoolCalendarApi',
  keepUnusedDataFor: 5,
  tagTypes: [SchoolCalendarTagTypes.SCHOOL_CALENDAR]
}).injectEndpoints({
  endpoints: builder => ({
    getSchoolCalendars: builder.query<
      SchoolCalendarListResponse,
      FetchSchoolCalendarListParams | undefined
    >({
      query: params => ({
        url: '/api/v1/school-calendar/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ id }) => ({ type: SchoolCalendarTagTypes.SCHOOL_CALENDAR, id }) as const
              ),
              { type: SchoolCalendarTagTypes.SCHOOL_CALENDAR, id: RTKCacheTags.LIST }
            ]
          : [{ type: SchoolCalendarTagTypes.SCHOOL_CALENDAR, id: RTKCacheTags.LIST }]
    }),
    getSingleSchoolCalendar: builder.query<SchoolCalendar, string>({
      query: id => `/api/v1/school-calendar/${id}/`,
      providesTags: (result, _error, id) =>
        result ? [{ type: SchoolCalendarTagTypes.SCHOOL_CALENDAR, id }] : []
    }),
    createSingleSchoolCalendar: builder.mutation<SchoolCalendar, Partial<SchoolCalendar>>({
      query: data => ({
        url: '/api/v1/school-calendar/',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: SchoolCalendarTagTypes.SCHOOL_CALENDAR, id: RTKCacheTags.LIST }]
    }),
    updateSingleSchoolCalendar: builder.mutation<
      SchoolCalendar,
      { id: string } & Partial<SchoolCalendar>
    >({
      query: ({ id, ...data }) => ({
        url: `/api/v1/school-calendar/${id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: SchoolCalendarTagTypes.SCHOOL_CALENDAR, id }
      ]
    }),
    deleteSingleSchoolCalendar: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/school-calendar/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: SchoolCalendarTagTypes.SCHOOL_CALENDAR, id: RTKCacheTags.LIST }]
    })
  })
})

export const {
  useGetSchoolCalendarsQuery,
  useGetSingleSchoolCalendarQuery,
  useCreateSingleSchoolCalendarMutation,
  useUpdateSingleSchoolCalendarMutation,
  useDeleteSingleSchoolCalendarMutation
} = schoolCalendarApi
